/**
 * ForecastFlowTextSummary — "Smart Summary" for forecast sections
 *
 * Generates a plain-language summary of a flow forecast, shown inline in the
 * Short-Term and Seasonal forecast sections when the user clicks
 * "Generate Smart Summary".
 *
 * The summary has three parts:
 *
 * 1. LTA comparison — compares the forecasted average over the horizon to the
 *    long-term average (LTA) for the same period. Reports an explicit percentage
 *    (e.g. "25% above the long-term average") or "about the same" if within ±10%.
 *
 * 2. Trend detection — analyzes the full forecast time series (not just endpoints)
 *    to detect meaningful patterns:
 *    - Peak-then-decline: a rise of >10% from start followed by a fall of >10%
 *      from the peak before the end of the horizon.
 *    - Trough-then-rise: a fall of >10% from start followed by a rise of >10%
 *      from the trough.
 *    - Monotonic increase/decrease: a >10% net change from start to end when
 *      no peak/trough pattern is detected.
 *    - Near-zero guard: if the starting value is <0.01, trend is omitted to
 *      avoid misleading percentage comparisons.
 *    When a peak or trough is detected, the summary includes approximate timing
 *    (e.g. "peaking around April 12") using the forecast's validTimes.
 *
 * 3. Min/max expected discharge — reports the minimum and maximum forecasted
 *    values with percentage change from the previous forecast.
 *
 * Each forecast section (ShortTerm, Seasonal3) renders this component with the
 * appropriate source and horizon. The horizon matches the section's actual
 * display range (10 days for short-term, site-specific for seasonal via
 * getSeasonalHorizon).
 */
import {last, max, min, round, sum} from 'lodash';
import React from 'react';

import {useUnitSystem} from 'src/contexts/UnitSystemContext';
import useForecastTimes from 'src/hooks/useForecastTimes';
import {Site} from 'src/lib/api/project';
import {
  ApiForecast,
  ApiForecastTimeSeries,
  ApiObservationTimeSeries,
  DashboardSection,
  FlowForecastColumns,
  FlowHistoricalDailyColumns,
  Source,
  UnitSystem,
} from 'src/lib/api/series';
import {addDays, formatDateDay} from 'src/lib/dates';
import {getHasHFShortTermMean} from 'src/lib/siteUtils';

import {recordEvent} from './Analytics';
import TimeSeriesProvider from './TimeSeriesProvider';
import {Translator, useTranslations} from './TranslationsContext';
import {makeConfigItems, makeRequestFromVizConfig} from './Viz/config';

const TREND_THRESHOLD = 0.1;

type Props = {
  site: Site;
  source: Source.HydroForecastShortTerm | Source.HydroForecastSeasonal;
  horizonDays: number;
  section: DashboardSection;
};

export function SmartSummaryButton({siteId, onClick}: {siteId: string; onClick: () => void}) {
  const {t} = useTranslations();
  return (
    <button
      onClick={() => {
        onClick();
        recordEvent('generate_smart_summary_clicked', {siteId});
      }}
      className="bg-blue-50 text-blue-600 py-1 px-2 rounded inline-flex items-center"
    >
      <svg viewBox="0 0 20 20" className="w-4 h-4 mr-1">
        <g>
          <polygon
            fill="rgba(37, 99, 235, var(--tw-text-opacity))"
            points="7 3.55271368e-15 5 5 0 6.99807408 5 9 7 14 9 9 14 7.00528342 9 5"
          />
          <polygon
            fill="rgba(37, 99, 235, var(--tw-text-opacity))"
            points="15 10 13.5 13.4963055 10 14.9948544 13.5 16.4931388 15 20 16.5 16.4931388 20 15 16.5 13.4963055"
          />
        </g>
      </svg>
      {t('generateSmartSummary')}
    </button>
  );
}

export default function ForecastFlowTextSummary({site, source, horizonDays, section}: Props) {
  const {t, locale} = useTranslations();
  const unitSystem = useUnitSystem();
  const [initTimeStartDate, initTimeEndDate] = React.useMemo(() => {
    return [addDays(new Date(), -3), new Date()];
  }, []);

  const forecastTimesRequest = useForecastTimes(
    site.id,
    [source],
    initTimeStartDate,
    initTimeEndDate,
    false
  );

  const dateRange = React.useMemo(
    () => [new Date(), addDays(new Date(), horizonDays)],
    [horizonDays]
  ) as [Date, Date];

  const forecastTimesBySource = forecastTimesRequest.data;
  if (!forecastTimesBySource || !forecastTimesBySource[source]?.length) {
    return <></>;
  }

  const forecastColumn = getHasHFShortTermMean(site.forecastTimeSeries)
    ? FlowForecastColumns.DISCHARGE_MEAN
    : FlowForecastColumns.DISCHARGE_MEDIAN;

  const config = makeConfig(forecastColumn, source);

  return (
    <div>
      <TimeSeriesProvider
        site={site}
        section={section}
        forecastTimesBySource={{
          [source]: [forecastTimesBySource[source]![0]],
        }}
        dateRange={dateRange}
        {...makeRequestFromVizConfig(config)}
      >
        {(vizProps) => {
          if (!vizProps.isLoading) {
            if (vizProps.forecastTimeSeriesList.length === 0) {
              // rare case where maybe a forecast is being backfilled and its issue time is valid but the api doesn't return a forecast
              return <div>{t('forecastInProgress')}</div>;
            } else if (vizProps.observationTimeSeriesList.length === 0) {
              // the site may not have LTA, in that case, hide?
              return <></>;
            }
          }

          // placeholder/loading for each text block
          let flowSummary = (
            <span className="inline-block w-96 h-6 bg-blue-100 rounded-sm -mb-2"></span>
          );
          let dischargeRange = (
            <span className="inline-block w-48 h-6 bg-blue-100 rounded-sm -mb-2"></span>
          );

          if (!vizProps.isLoading) {
            const forecastTS = vizProps.forecastTimeSeriesList[0];
            const observationTS = vizProps.observationTimeSeriesList[0];

            flowSummary = getFlowSummaryDescription(
              t,
              locale,
              forecastColumn,
              forecastTS,
              observationTS
            );

            dischargeRange = getDischargeRangeDescription(forecastColumn, forecastTS, unitSystem);
          }

          return (
            <div>
              <p>{t('overNextDays', {horizonDays: horizonDays.toString()})}</p>
              <p>➙ {flowSummary}.</p>
              <p>
                ➙ {t('dischargeRange')} {dischargeRange}.
              </p>
            </div>
          );
        }}
      </TimeSeriesProvider>
    </div>
  );
}

function getLTAText(
  t: Translator,
  forecastColumn: FlowForecastColumns.DISCHARGE_MEAN | FlowForecastColumns.DISCHARGE_MEDIAN,
  forecastTimeSeries: ApiForecastTimeSeries,
  observationTimeSeries: ApiObservationTimeSeries
): string {
  const forecastData = last(forecastTimeSeries.forecasts)!.data[forecastColumn];
  const ltaData = observationTimeSeries.data[FlowHistoricalDailyColumns.FLOW_MEAN];

  const n = Math.min(forecastData.length, ltaData.length);
  const forecastSum = sum(forecastData.slice(0, n));
  const ltaSum = sum(ltaData.slice(0, n));

  if (ltaSum === 0) {
    return t('aboutSameLTA');
  }

  const pctDiff = round(((forecastSum - ltaSum) / ltaSum) * 100);

  if (Math.abs(pctDiff) <= 10) {
    return t('aboutSameLTA');
  } else if (pctDiff > 0) {
    return t('pctAboveLTA', {pct: Math.abs(pctDiff).toString()});
  } else {
    return t('pctBelowLTA', {pct: Math.abs(pctDiff).toString()});
  }
}

function getTrendSuffix(
  t: Translator,
  locale: string,
  forecastColumn: FlowForecastColumns.DISCHARGE_MEAN | FlowForecastColumns.DISCHARGE_MEDIAN,
  forecastTimeSeries: ApiForecastTimeSeries
): string {
  const forecast = last(forecastTimeSeries.forecasts)!;
  const data = forecast.data[forecastColumn];
  const validTimes = forecast.validTimes;

  if (data.length < 2) {
    return '';
  }

  const startVal = data[0];
  const endVal = data[data.length - 1];
  const maxVal = max(data)!;
  const minVal = min(data)!;
  const maxIdx = data.indexOf(maxVal);
  const minIdx = data.indexOf(minVal);

  if (startVal == null || endVal == null || startVal < 0.01) {
    return '';
  }

  const changePct = (endVal - startVal) / startVal;
  const peakPct = (maxVal - startVal) / startVal;
  const troughPct = (startVal - minVal) / startVal;

  // Peak: significant rise then fall
  if (
    peakPct > TREND_THRESHOLD &&
    maxIdx < data.length - 2 &&
    maxVal > 0 &&
    (maxVal - endVal) / maxVal > TREND_THRESHOLD
  ) {
    const peakDate = validTimes[maxIdx] ? formatDateDay(validTimes[maxIdx], locale) : '';
    return peakDate ? t('peakingAround', {date: peakDate}) : '';
  }

  // Trough: significant fall then rise
  if (
    troughPct > TREND_THRESHOLD &&
    minIdx < data.length - 2 &&
    minVal > 0 &&
    (endVal - minVal) / minVal > TREND_THRESHOLD
  ) {
    const troughDate = validTimes[minIdx] ? formatDateDay(validTimes[minIdx], locale) : '';
    return troughDate ? t('reachingLowAround', {date: troughDate}) : '';
  }

  // Simple monotonic
  if (changePct > TREND_THRESHOLD) {
    return t('andForecasted', {trend: t('increase')});
  } else if (changePct < -TREND_THRESHOLD) {
    return t('andForecasted', {trend: t('decrease')});
  }

  return '';
}

function getFlowSummaryDescription(
  t: Translator,
  locale: string,
  forecastColumn: FlowForecastColumns.DISCHARGE_MEAN | FlowForecastColumns.DISCHARGE_MEDIAN,
  forecastTimeSeries: ApiForecastTimeSeries,
  observationTimeSeries: ApiObservationTimeSeries
): JSX.Element {
  const ltaText = getLTAText(t, forecastColumn, forecastTimeSeries, observationTimeSeries);
  const trendSuffix = getTrendSuffix(t, locale, forecastColumn, forecastTimeSeries);

  return (
    <span>
      {t('forecastedToAverage')} {ltaText}
      {trendSuffix && <>, {trendSuffix}</>}
    </span>
  );
}

function getMinMaxDischarge(
  forecastColumn: FlowForecastColumns.DISCHARGE_MEAN | FlowForecastColumns.DISCHARGE_MEDIAN,
  forecast: ApiForecast
): [number, number] {
  const data = forecast.data[forecastColumn];
  if (data.length === 0) {
    return [0, 0];
  }
  return [round(min(data)!), round(max(data)!)];
}

function formatDischarge(value: number, unit: string): JSX.Element {
  return (
    <span>
      {value.toLocaleString()} {unit}
      <sup>3</sup>/s
    </span>
  );
}

function getDischargeRangeDescription(
  forecastColumn: FlowForecastColumns.DISCHARGE_MEAN | FlowForecastColumns.DISCHARGE_MEDIAN,
  forecastTimeSeries: ApiForecastTimeSeries,
  unitSystem: UnitSystem
): JSX.Element {
  const [dischargeMin, dischargeMax] = getMinMaxDischarge(
    forecastColumn,
    last(forecastTimeSeries.forecasts)!
  );
  const unit = unitSystem === UnitSystem.Metric ? 'm' : 'ft';

  return (
    <>
      {formatDischarge(dischargeMin, unit)} to {formatDischarge(dischargeMax, unit)}
    </>
  );
}

function makeConfig(
  forecastColumn: FlowForecastColumns.DISCHARGE_MEAN | FlowForecastColumns.DISCHARGE_MEDIAN,
  source: Props['source']
) {
  return {
    forecasts: makeConfigItems(
      'forecasts',
      [
        {
          displayName: 'HydroForecast mean',
          source,
          // we have to get both, in case one is missing (some deprecated forecasts only produced median)
          columns: [forecastColumn],
          // empty object here because we are not styling anything using this config
          getStyle: () => ({}),
          legendIcon: 'line',
        },
      ],
      {}
    ),
    observations: makeConfigItems(
      'observations',
      [
        {
          displayName: 'Historical mean',
          source: Source.FLOW_HISTORICAL_DAILY,
          columns: [FlowHistoricalDailyColumns.FLOW_MEAN],
          // empty object here because we are not styling anything using this config
          getStyle: () => ({}),
          legendIcon: 'line',
        },
      ],
      {}
    ),
  };
}
